import i18n from 'i18n-js';
import React from 'react';
import { StyleSheet, Text, View, Platform, TouchableOpacity } from 'react-native';

import { moderateScale, isPhone, removeEmailFromValue, isWeb } from '@helpers';
import { getItemStyle, getImageSize, buildImageData, formatCreatedOn, buildTagsText } from '@components/search/images/helpers';

import ColorWrapper from '@styles/colors';
import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { scale as initialScale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';

import { DOC_TYPE } from '@components/imageHandling/common';

import Image from '@v2/context/Image/context';
import { SCREEN_PADDING_HORIZONTAL, TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import Preview from '@components/imageHandling/components/thumbnails';

const Item = ({
  isEmpty,
  itemSize = TILE_CONFIG.BASE_WIDTH,
  item,
  C,
  index,
  generateImageURL,
  isSmallMode,
  isLineMode,
  recentlyViewed,
  setRecentlyViewed,
  attachMode,
  containerWidth,
  withoutHorizontalPaddings,
  onPress,
  scale = initialScale,
  font = initialFont,
}) => {
  const style = getItemStyle(isEmpty, itemSize, isSmallMode, index, scale);
  const imgSize = getImageSize(itemSize, isLineMode, scale);
  const createdOn = formatCreatedOn(item.createdOn);

  const renderTags = () => {
    const t = item.links;

    if (!t || !t.length) {
      return (
        <Text numberOfLines={1} style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]}>
          {i18n.t('Not Tagged')}
        </Text>
      );
    }

    const isFavorite = t.find((i) => i.isFavorite);

    const arr = buildTagsText(t);
    return (
      <View style={{ flexDirection: 'row', gap: scale(5) }}>
        <View style={{ justifyContent: 'center' }}>
          <Svg icon={isFavorite ? 'starSelected' : 'clip'} width={scale(14)} height={scale(14)} color="Label/Light/Tertiary" />
        </View>
        <View style={{ flex: 1 }}>
          <Text numberOfLines={1} style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]}>
            {arr}{' '}
          </Text>
        </View>
      </View>
    );
  };

  const renderContext = () => {
    return (
      <View style={styles.itemTextBlock}>
        <View style={styles.lineContainer}>
          <Text numberOfLines={1} style={[{ color: C('Label / Light / Primary') }, font.Headline]}>
            {item.title}
          </Text>
        </View>
        <Text numberOfLines={1} style={[styles.itemDescription, styles.itemLightText, { color: C('Label/Light/Tertiary') }, font['Subhead 1']]}>
          {removeEmailFromValue(item.subTitle)}
        </Text>
        <Text numberOfLines={1} style={[styles.itemDescription, styles.itemLightText, { color: C('Label/Light/Tertiary') }, font['Subhead 1']]}>
          {i18n.t(item.documentType === 'Other' ? DOC_TYPE.GENERAL_DOCUMENT : item.documentTag ? item.documentTag : DOC_TYPE.IMAGE)} - {createdOn}
        </Text>
        <View style={[styles.itemDescription]}>{renderTags()}</View>
      </View>
    );
  };

  const imageData = buildImageData(item, imgSize, () => onPress({ image: item, showActions: false }), attachMode);

  if (isLineMode) {
    if (isEmpty) return null;

    return (
      <View style={{ width: '100%', alignItems: 'center' }}>
        <View style={{ width: containerWidth }}>
          <TouchableElement onPress={() => onPress({ image: item, showActions: true })}>
            {({ boxStyle }) => (
              <View
                style={[
                  boxStyle,
                  {
                    height: scale(60),
                    paddingHorizontal: scale(!withoutHorizontalPaddings ? SCREEN_PADDING_HORIZONTAL : 0),
                  },
                ]}
              >
                <View style={{ width: '100%', flexDirection: 'row', flexDirection: 'row', alignItems: 'center', overflow: 'hidden' }}>
                  <View style={{ width: scale(TILE_CONFIG.LINE_MODE_WIDTH), height: scale(TILE_CONFIG.LINE_MODE_HEIGHT) }}>
                    <Image {...imageData} scale={scale} font={font} />
                  </View>
                  <View style={{ flex: 1, paddingLeft: scale(10) }}>
                    <Text numberOfLines={1}>
                      <Text style={[font['Button 1'], { color: C('Label/Light/Primary') }]}>{item.fileName} </Text>
                      <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{item.filePath ? `- ${item.filePath}` : ''}</Text>
                    </Text>
                  </View>
                </View>
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
    );
  }

  const isDocument = item.isDocument || item.documentType === 'Other';

  return isEmpty ? (
    <View style={[styles.itemComponent, style]} />
  ) : (
    <View style={[styles.itemComponent, style]}>
      <TouchableElement onPress={() => (isDocument ? onPress({ image: item, showActions: false }) : () => {})}>
        {({ boxStyle }) => (
          <View style={[imgSize, { backgroundColor: C('Background/Light/Tile No Picture'), borderRadius: scale(TILE_CONFIG.BORDER_RADIUS) }, isDocument && boxStyle]}>
            {isDocument && (
              <Preview
                item={{ ...item, isDocument: true }}
                scale={scale}
                font={font}
                withoutDownloadButton
                withoutFileName
                borderRadius={scale(TILE_CONFIG.BORDER_RADIUS)}
                bg={C('Overlay/Light/Dim light')}
              />
            )}
            {!isDocument && <Image {...imageData} scale={scale} font={font} />}
          </View>
        )}
      </TouchableElement>
      {!isSmallMode && (
        <View>
          <View style={styles.itemImgMarksWrapper}>
            {item.new == 1 ? (
              <View style={[styles.itemStatusMark, { backgroundColor: C('Background / Light / New flag') }]}>
                <Text style={[styles.itemStatusMarkText, { color: C('Label / Light / AlwaysDark Primary') }, font.Small]}>{i18n.t('New')}</Text>
              </View>
            ) : (
              <View />
            )}
          </View>
          <TouchableOpacity activeOpacity={1} onPress={() => onPress({ image: item, showActions: true })}>
            {renderContext()}
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

export default ColorWrapper(Item);

const styles = StyleSheet.create({
  lineContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  itemComponent: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: moderateScale(isPhone ? 24 : 29),
  },
  itemImgMarksWrapper: {
    display: 'flex',
    paddingLeft: 9,
    paddingRight: 9,
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 21,
    position: 'relative',
    top: -27,
    flexDirection: 'row',
  },
  itemStatusMark: {
    paddingLeft: 1,
    paddingRight: 1,
    height: 15,
    borderRadius: 2,
    marginTop: Platform.OS === 'android' ? 0 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemStatusMarkText: {
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: moderateScale(9.5),
    marginTop: Platform.OS === 'android' ? -1 : 1,
    letterSpacing: moderateScale(0.02),
    textTransform: 'uppercase',
    paddingLeft: 3,
    paddingRight: 3,
  },
  itemDateIcon: {
    height: 13,
    width: 13,
    marginLeft: Platform.OS === 'android' ? 1 : 0,
    marginTop: 1,
  },
  itemDateMark: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 11,
    height: 22,
    paddingLeft: 8,
    paddingRight: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  itemDate: {
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: moderateScale(16),
    lineHeight: moderateScale(19),
    letterSpacing: moderateScale(-0.3),
    marginLeft: 4,
  },

  itemTextBlock: {
    paddingLeft: 9,
    paddingTop: 7,
    position: 'relative',
    top: -21,
  },
  itemHeader: {
    display: 'flex',
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: !isPhone ? moderateScale(17) : 18,
    lineHeight: moderateScale(20),
    letterSpacing: moderateScale(0.02),
  },
  itemHeaderSize: {
    marginLeft: 7,
  },
  itemLightText: {
    fontFamily: Platform.OS === 'android' ? 'Roboto' : 'System',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: !isPhone ? moderateScale(15) : 16,
    lineHeight: moderateScale(18),
    letterSpacing: moderateScale(0.01),
  },
  itemDescription: {
    marginTop: 5,
  },
  itemPart: {
    marginTop: 5,
  },
  itemDrawing: {
    marginTop: 5,
  },
});
